// UI primitives
import { Field, FieldLabel, FieldContent } from '@/components/ui/field';

// Shared components
import { toggleChipClassName } from '@components/common/toggleChip';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Vocabulary — the single source shared with Ruby (config/event_tags.json).
// To add a festive period: one entry in that file + its label under
// order_details.event_tags in en.yml. Outside its window a holiday tag
// disappears from the form (but stays visible on an event that already carries
// it). Internal tokens are never offered or drawn.
import {
  PERMANENT_EVENT_TAGS,
  HOLIDAY_TAGS,
  INTERNAL_EVENT_TAGS,
  WEDDING_TAG,
} from '@/react/constants/eventTags';

// Re-exported so behavioral call sites (e.g. eventChipStyles) keep importing it
// from here.
export { WEDDING_TAG, PERMANENT_EVENT_TAGS, HOLIDAY_TAGS };

/** Resolve a Papercut-aligned occasion token (e.g. "weddingEvent") to its display label. */
export function useEventTagLabel() {
  const { t } = useTranslation();
  return (token: string) => t(`event_tags.${token}`, token);
}

// Today as YYYY-MM-DD in Singapore (en-CA formats ISO-style), so windows flip at
// SGT midnight regardless of the viewer's machine timezone. Lexicographic
// comparison is safe on ISO dates.
function sgToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore' }).format(new Date());
}

interface EventTagsFieldProps {
  label: string;
  value: string[];
  onChange: (tags: string[]) => void;
  labelClassName?: string;
}

/**
 * Toggle-button list of event tags, shared by the event creation modal and the
 * edit-event form so both stay in sync. Selection toggles the tag in/out of the
 * `value` array. Permanent tags are always offered; festive tags only inside
 * their window. Any tag already on the event remains visible and removable,
 * including out-of-window or unknown tags.
 */
export function EventTagsField({ label, value, onChange, labelClassName }: EventTagsFieldProps) {
  const getEventTagLabel = useEventTagLabel();

  const today = sgToday();
  const inWindow = HOLIDAY_TAGS.filter((h) => h.startDate <= today && today <= h.endDate).map(
    (h) => h.id,
  );
  const offered = [...PERMANENT_EVENT_TAGS, ...inWindow];
  // Show offered tags plus any already-attached extra, but never draw internal
  // Papercut flags. They stay in `value` (so a save preserves them) — they're
  // just not rendered or toggleable here.
  const extras = value.filter(
    (tag) => !offered.includes(tag) && !INTERNAL_EVENT_TAGS.includes(tag),
  );
  const visibleTags = [...offered, ...extras];

  return (
    <Field>
      <FieldLabel className={labelClassName}>{label}</FieldLabel>
      <FieldContent>
        <div className="flex flex-wrap gap-3">
          {visibleTags.map((tag) => {
            const isSelected = value.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                aria-pressed={isSelected}
                onClick={() =>
                  onChange(isSelected ? value.filter((existing) => existing !== tag) : [...value, tag])
                }
                className={toggleChipClassName(isSelected)}
              >
                {getEventTagLabel(tag)}
              </button>
            );
          })}
        </div>
      </FieldContent>
    </Field>
  );
}
